import {User} from '@/api/models'
import Coach from '@/api/coach'
import axios from 'axios'
import jwt from 'jsonwebtoken'

export default {
  store: localStorage,
  getHttpOptions () {
    const token = this.store.getItem('currentToken')
    const options = {
      baseURL: process.env.API_URL,
      headers: {'jwt-auth': token}
    }
    return options
  },
  ping () {
    return axios
      .get('ping', this.getHttpOptions())
      .then((res) => { return res.status === 200 })
      .catch((e) => { return false })
  },
  auth (username, password) {
    return axios.post('auth/login', {
      username: username,
      password: password
    }, this.getHttpOptions())
      .then((res) => {
        return {
          user: new User().load(res.data.user),
          token: res.data.token,
          role: res.data.role
        }
      })
      .catch((error) => {
        if (error.response) {
          throw new Error(error.response.data)
        } else {
          console.log(error.message)
          throw new Error('Oh! Sorry for the service error')
        }
      })
  },
  register (username, password, data) {
    return axios.post('auth/register', {
      username: username,
      password: password,
      data: data
    }, this.getHttpOptions())
      .then((res) => {
        return {
          user: new User().load(res.data.user),
          token: res.data.token
        }
      })
      .catch((error) => {
        if (error.response) {
          throw new Error(error.response.data)
        } else {
          console.log(error.message)
          throw new Error('Oh! Sorry for the service error')
        }
      })
  },
  setCurrentUser (me) {
    this.store.setItem('currentUser', me.user.dumps())
    this.store.setItem('currentToken', me.token)
  },
  updateCurrentUser (user) {
    this.store.setItem('currentUser', user.dumps())
  },
  getCurrentUser () {
    var data = this.store.getItem('currentUser')
    if (data) {
      var me = new User().loads(data)
      return me
    } else {
      return undefined
    }
  },
  hasCurrentUser () {
    return this.store.getItem('currentUser') !== null
  },
  removeCurrentUser () {
    this.store.removeItem('currentUser')
    this.store.removeItem('currentToken')
  },
  hasRole (name) {
    const token = this.store.getItem('currentToken')
    const decoded = jwt.decode(token)
    if (!decoded.roles) return false
    return decoded.roles.includes(name)
  },
  update (me) {
    this.updateCurrentUser(me)
    axios.post('/athlete', me.dump(), this.getHttpOptions()) // push to server - async call of server
      .catch((e) => console.log(e.message))
    return me
  },
  async loadResource (path) {
    await axios.get(path, this.getHttpOptions()).then(async (res) => {
      await this.store.setItem('res/' + path, JSON.stringify(res.data))
    }).catch((e) => console.log(e.message))
  },
  loadLocalResource (name) {
    try {
      return JSON.parse(this.store.getItem(name))
    } catch (e) {
      console.log('Unable to read local resource: ' + name)
      return undefined
    }
  },
  cacheLoad () {
    this.loadResource('activity/skill/list')
    this.loadResource('activity/list')
    this.loadResource('activity/programs/taskTypes')
    this.loadResource('activity/programs/list')

    //  load dynamically the programs based on the program list
    this.getProgramList().forEach((item, index) => {
      const name = item[0]
      this.loadResource('activity/program/' + name)
    })
  },
  changePassword (me, newPassword) {
    return axios.post('/auth/changePassword', {
      username: me.getUsername(),
      newPassword: newPassword
    }, this.getHttpOptions())
  },
  async changeUsername (me, newUsername) {
    await axios.post('/auth/changeUsername', {
      previousUsername: me.getUsername(),
      newUsername: newUsername
    }, this.getHttpOptions())
    me.setUsername(newUsername)
    this.update(me)
  },

  getSkills () {
    return this.loadLocalResource('res/activity/skill/list')
  },
  async updateSkills (skills) {
    // call service to store the skills
    axios.post('/activity/skill/list', skills, this.getHttpOptions())
    // update the local resource
    await this.store.setItem('res/activity/skill/list', JSON.stringify(skills))
  },
  getProgramList () {
    return this.loadLocalResource('res/activity/programs/list')
  },
  async updateProgramList (programs) {
    axios.post('/activity/programs/list', programs, this.getHttpOptions())
    await this.store.setItem('res/activity/programs/list', JSON.stringify(programs))
  },
  getActivityList (forceLoad = false) {
    if (forceLoad) this.loadResource('activity/list')
    return this.loadLocalResource('res/activity/list')
  },
  async updateActivityList (activities) {
    axios.post('/activity/list', activities, this.getHttpOptions())
    await this.store.setItem('res/activity/list', JSON.stringify(activities))
  },

  getProgram (name) {
    const programs = this.getProgramList()
    const desc = programs.filter(item => item[0] === name)[0] // return array [key, program desc]
    var program = {}
    program.name = name
    program.description = desc[1]
    program.sessions = this.loadLocalResource('res/activity/program/' + name)
    if (!program.sessions) {
      program.sessions = {}
    }
    return program
  },
  async updateProgram (name, program) {
    // Update program description into the list
    var programs = this.getProgramList()
    const index = programs.findIndex(item => item[0] === name) // return array [key, program desc]
    programs[index][0] = program.name
    programs[index][1] = program.description
    this.updateProgramList(programs)

    // call service to store the program sessions
    await axios.post('/activity/program/' + program.name, program.sessions, this.getHttpOptions())
    // update the local resource
    this.store.setItem('res/activity/program/' + program.name, JSON.stringify(program.sessions))
  },

  getProgramTaskTypes () {
    return this.loadLocalResource('res/activity/programs/taskTypes')
  },
  getNextSessions (me) {
    if (me.athlete.gotoNextSessions || me.athlete.forceSessionRefresh) {
      // Update the next sessions
      if (!me.athlete.forceSessionRefresh) me.athlete.progSeqNum++ // incr the seq counter
      me.athlete.nextSessions = Coach.createWeekPlan(me)
      me.athlete.forceSessionRefresh = false // always reset to false forceRefresh
      me.athlete.gotoNextSessions = false // always reset to false
      this.update(me)
    }
    // return only the non completed sessions
    return this.getRemainingSessions(me)
  },
  getRemainingSessions (me) {
    return Object.values(me.athlete.nextSessions).filter(session => {
      if (!session.status) return true
      return (session.status !== 'Completed')
    })
  },
  getPastSessions (me) {
    const url = 'athlete/' + me.id() + '/past-session/list'
    return axios.get(url, this.getHttpOptions()).then((res) => { return res.data })
  },
  async addPastSession (me, session) {
    var sessions = this.getPastSessions(me)
    sessions[session.id] = session
    await axios.post('athlete/' + me.id() + '/past-session/', session, this.getHttpOptions())
  },
  getPastSession (me, id) {
    const url = 'athlete/' + me.id() + '/past-session?sessionId=' + id
    return axios.get(url, this.getHttpOptions()).then((res) => { return res.data })
  }
}



// WEBPACK FOOTER //
// ./src/api/index.js